<template>
  <div class="auth-form w-100">
    <v-btn
      v-if="!hideNavigationActions"
      variant="text"
      class="text-medium-emphasis px-0 mb-2 justify-start"
      :prepend-icon="backIcon"
      @click="$emit('back')"
    >
      Volver al inicio
    </v-btn>

    <h1
      class="auth-form__title text-h5 font-weight-semibold mb-2 d-flex align-center"
    >
      <v-icon :icon="calendarIcon" class="mr-2" />
      Turno rápido
    </h1>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Ingrese sus datos para solicitar un turno.
    </p>

    <div v-if="mobileHelpHtml" class="mobile-help d-lg-none mt-4 mb-6">
      <v-expansion-panels variant="accordion" class="mobile-help__panels">
        <v-expansion-panel elevation="0" class="mobile-help__panel">
          <v-expansion-panel-title class="mobile-help__title">
            <div class="d-flex align-center min-width-0">
              <v-icon :icon="helpIcon" size="18" class="mobile-help__icon mr-2" />
              <span class="text-body-2 font-weight-semibold text-truncate">
                ¿Necesita ayuda para sacar turno?
              </span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="mobile-help__text">
            <div class="mobile-help-html text-body-2" v-html="mobileHelpHtml" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <v-form ref="authForm" @submit.prevent="handleSubmit">
      <div class="stack-fields">
        <TipoDocumentoTextField
          v-model:tipo-id="documentTipoId"
          v-model:numero="documentNumber"
          @tipo-change="onTipoChange"
          :rules-nro-doc="[rules.required]"
        />
        <DatePicker
          v-model="fechaNacimiento"
          label="Fecha de nacimiento"
          required
          hide-details="auto"
        />
        <v-select
          v-model="sexo"
          label="Género"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :items="opcionesGenero"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
        />

        <div class="d-flex flex-wrap gap-3 mt-4">
          <v-btn
            v-if="!hideNavigationActions"
            variant="outlined"
            type="button"
            @click="$emit('back')"
          >
            Cancelar
          </v-btn>
          <v-btn
            type="submit"
            class="flex-grow-1"
            min-width="120"
            color="primary"
            :loading="isLoading"
          >
            Buscar turnos
          </v-btn>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import enums from '../../utils/enums'
import rules from '../../utils/helpers/rules'
import TipoDocumentoTextField from '../shared/TipoDocumentoTextField.vue'
import DatePicker from '../shared/DatePicker.vue'

export default {
  name: 'AutogestionarTurnoForm',
  components: { TipoDocumentoTextField, DatePicker },
  props: {
    hospitalConfig: {
      type: Object,
      required: true,
    },
    hideNavigationActions: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['back'],
  data() {
    return {
      backIcon: enums.icons.BACK,
      calendarIcon: enums.icons.CALENDAR,
      helpIcon: enums.icons.HELP_OUTLINE,
      documentTipoId: null,
      documentNumber: '',
      tipoDocLabel: '',
      fechaNacimiento: '',
      sexo: '',
      opcionesGenero: [
        { value: 'F', title: 'Femenino' },
        { value: 'M', title: 'Masculino' },
      ],
      isLoading: false,
      rules,
    }
  },
  computed: {
    mobileHelpHtml() {
      return String(
        this.hospitalConfig?.helpTextAutogestion
        || this.hospitalConfig?.helpTextLogin
        || '',
      ).trim()
    },
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      requestTemporalSession: 'guestSession/requestTemporalSession',
      fetchPortalPrivateParams: 'portalHome/fetchPortalPrivateParams',
      validarLimiteTurnosPorPaciente:
        'asistenteTurnos/validarLimiteTurnosPorPaciente',
    }),
    ...mapMutations({
      setGuestSession: 'guestSession/setGuestSession',
      resetGuestSession: 'guestSession/resetGuestSession',
    }),
    onTipoChange(item) {
      this.tipoDocLabel = String(item?.value ?? '').trim()
    },
    async handleSubmit() {
      const { valid } = await this.$refs.authForm.validate()
      if (!valid) return
      if (this.documentTipoId == null) {
        this.setAlert({
          type: 'warning',
          message: 'Seleccione el tipo de documento.',
        })
        return
      }
      this.isLoading = true
      try {
        try {
          await this.fetchPortalPrivateParams()
        } catch {
          // Si no se puede cargar la configuracion, se usa el fallback del flujo.
        }

        // Cualquier sesión normal previa queda invalidada al entrar como invitado:
        // así evitamos que el interceptor mande un token regular en vez del temporal.
        try {
          window.localStorage.removeItem('token')
        } catch {
          // Sin localStorage disponible: no es crítico.
        }

        // Inicializamos la sesión invitada con los datos del documento.
        // El accessToken se completa luego con la respuesta del back.
        this.setGuestSession({
          activo: true,
          tdId: this.documentTipoId,
          nroDoc: String(this.documentNumber || '').trim(),
          tipoDocLabel: this.tipoDocLabel,
          fechaNacimiento: String(this.fechaNacimiento || '').trim(),
          sexo: String(this.sexo || '').trim(),
          nombre: '',
          apellido: '',
          email: '',
          celular: '',
          telefono: '',
          accessToken: '',
          sessionId: '',
          expiresAt: 0,
          fichaId: null,
          sinFichaAutogestion: false,
        })
        const res = await this.requestTemporalSession()
        if (!res?.ok) {
          this.resetGuestSession()
          this.setAlert({
            type: 'error',
            message:
              res?.mensaje
              || 'No se pudo iniciar la sesión para sacar turno. Intente nuevamente.',
          })
          return
        }

        try {
          const limite = await this.validarLimiteTurnosPorPaciente()
          if (!limite.ok) {
            this.resetGuestSession()
            this.setAlert({ type: 'warning', message: limite.mensaje })
            return
          }
        } catch {
          this.resetGuestSession()
          this.setAlert({
            type: 'error',
            message:
              'No se pudo validar si puede solicitar un nuevo turno. Intente nuevamente.',
          })
          return
        }

        await this.$router.push({ name: 'portal-nuevo-turno' })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.auth-form {
  max-width: 28rem;
}
.auth-form__title {
  color: var(--portal-primary);
}
.mobile-help__panel {
  --mobile-help-bg: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.075);
  border-radius: 8px !important;
  color: var(--portal-primary);
  overflow: hidden;
  background: var(--mobile-help-bg) !important;
  box-shadow: none !important;
}
.mobile-help__title {
  min-height: 44px !important;
  padding: 0 14px !important;
  color: var(--portal-primary);
  background: var(--mobile-help-bg);
}
.mobile-help__title :deep(.v-expansion-panel-title__overlay) {
  opacity: 0;
}
.mobile-help__icon {
  color: var(--portal-primary);
}
.mobile-help__text :deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 14px 16px;
  border-top: 1px solid rgba(var(--v-theme-primary), 0.06);
  background: var(--mobile-help-bg);
}
.mobile-help-html {
  color: #000;
  line-height: 1.55;
}
.portal-app--dark .mobile-help-html {
  color: rgba(var(--v-theme-on-surface), 0.78);
}
.mobile-help-html :deep(p) {
  margin-bottom: 0.5rem;
}
.mobile-help-html :deep(p:last-child) {
  margin-bottom: 0;
}
.mobile-help-html :deep(ul) {
  list-style: disc outside;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
}
.mobile-help-html :deep(li) {
  margin-bottom: 0.25rem;
}
.mobile-help-html :deep(strong) {
  font-weight: 600;
}
.stack-fields > * + * {
  margin-top: 1.25rem;
}
.gap-3 {
  gap: 0.75rem;
}
.autogestion-info-alert :deep(.v-alert__content) {
  color: var(--portal-primary);
}
</style>
